// ============================================================================
// CONTRACTS HOOK — useSupabaseContracts
// ============================================================================
// Tables:  inquiry_contracts, contract_tc_templates
// Follows: useSupabase.js conventions (requireSession, formatSupabaseError,
//          normalizeId, camelCase ↔ snake_case mapping)
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { formatSupabaseError, normalizeId } from './useSupabase';

const requireSession = async () => {
  const { data: { session }, error: sessionError } = await supabase.auth.getSession();
  if (sessionError) {
    console.error('[requireSession] Session error:', sessionError);
    throw new Error('Authentication error: ' + sessionError.message);
  }
  if (!session || !session.user) {
    console.error('[requireSession] No active Supabase session');
    throw new Error(
      'Please sign in to perform this action. ' +
      'Note: The "switch user" feature only changes the UI view — ' +
      'it does not change your database permissions. ' +
      'You must sign in with your actual credentials.'
    );
  }
  return session.user.id;
};

// ── DB → APP ──
const mapContract = (r) => ({
  id: normalizeId(r.id),
  inquiryId: r.inquiry_id,
  contractName: r.contract_name ?? '',
  versionName: r.version_name ?? '',
  office: r.office ?? 'mumbai',
  status: r.status ?? 'draft',
  docType: r.doc_type ?? 'offer',
  areaStatementVersionId: r.area_statement_version_id ?? null,
  feeOfferIndex: r.fee_offer_index != null ? Number(r.fee_offer_index) : null,
  designBriefProjectType: r.design_brief_project_type ?? '',
  designBriefLocation: r.design_brief_location ?? '',
  designBriefCustomText: r.design_brief_custom_text ?? '',
  constructionCoordFee: r.construction_coord_fee != null ? Number(r.construction_coord_fee) : null,
  excludedServices: Array.isArray(r.excluded_services) ? r.excluded_services : [],
  feeStageOverrides: Array.isArray(r.fee_stage_overrides) ? r.fee_stage_overrides : [],
  signatoryName: r.signatory_name ?? '',
  signatoryTitle: r.signatory_title ?? '',
  signatoryDin: r.signatory_din ?? '',
  expensesText: r.expenses_text ?? '',
  tcTemplateId: r.tc_template_id != null ? normalizeId(r.tc_template_id) : null,
  tcCustomContent: r.tc_custom_content ?? '',
  tcType: r.tc_type ?? 'standard',
  sowTemplateId: r.sow_template_id ?? null,
  validityMonths: r.validity_months != null ? Number(r.validity_months) : 2,
  contractSettings: (r.contract_settings && typeof r.contract_settings === 'object') ? r.contract_settings : {},
  createdAt: r.created_at ?? null,
  updatedAt: r.updated_at ?? null,
});

const mapTemplate = (r) => ({
  id: normalizeId(r.id),
  label: r.label ?? '',
  office: r.office ?? 'both',
  content: r.content ?? '',
  isDefault: r.is_default ?? false,
  createdAt: r.created_at ?? null,
  updatedAt: r.updated_at ?? null,
});

// ── APP → DB (contracts) ──
const toContractDb = (d) => {
  const db = {};
  if (d.inquiryId !== undefined) db.inquiry_id = d.inquiryId;
  if (d.contractName !== undefined) db.contract_name = d.contractName || null;
  if (d.versionName !== undefined) db.version_name = d.versionName || null;
  if (d.office !== undefined) db.office = d.office;
  if (d.status !== undefined) db.status = d.status;
  if (d.docType !== undefined) db.doc_type = d.docType;
  if (d.areaStatementVersionId !== undefined) db.area_statement_version_id = d.areaStatementVersionId || null;
  if (d.feeOfferIndex !== undefined) db.fee_offer_index = d.feeOfferIndex != null ? d.feeOfferIndex : null;
  if (d.designBriefProjectType !== undefined) db.design_brief_project_type = d.designBriefProjectType || null;
  if (d.designBriefLocation !== undefined) db.design_brief_location = d.designBriefLocation || null;
  if (d.designBriefCustomText !== undefined) db.design_brief_custom_text = d.designBriefCustomText || null;
  if (d.constructionCoordFee !== undefined) db.construction_coord_fee = d.constructionCoordFee != null ? d.constructionCoordFee : null;
  if (d.excludedServices !== undefined) db.excluded_services = d.excludedServices || [];
  if (d.feeStageOverrides !== undefined) db.fee_stage_overrides = d.feeStageOverrides || [];
  if (d.signatoryName !== undefined) db.signatory_name = d.signatoryName || null;
  if (d.signatoryTitle !== undefined) db.signatory_title = d.signatoryTitle || null;
  if (d.signatoryDin !== undefined) db.signatory_din = d.signatoryDin || null;
  if (d.expensesText !== undefined) db.expenses_text = d.expensesText || null;
  if (d.tcTemplateId !== undefined) db.tc_template_id = d.tcTemplateId != null ? d.tcTemplateId : null;
  if (d.tcCustomContent !== undefined) db.tc_custom_content = d.tcCustomContent || null;
  if (d.tcType !== undefined) db.tc_type = d.tcType;
  if (d.sowTemplateId !== undefined) db.sow_template_id = d.sowTemplateId || null;
  if (d.validityMonths !== undefined) db.validity_months = d.validityMonths;
  if (d.contractSettings !== undefined) db.contract_settings = d.contractSettings || {};
  return db;
};

const toTemplateDb = (d) => {
  const db = {};
  if (d.label !== undefined) db.label = d.label;
  if (d.office !== undefined) db.office = d.office;
  if (d.content !== undefined) db.content = d.content;
  if (d.isDefault !== undefined) db.is_default = d.isDefault;
  return db;
};

export const useSupabaseContracts = () => {
  const [contracts, setContracts] = useState([]);
  const [tcTemplates, setTcTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [{ data: cData, error: cErr }, { data: tData, error: tErr }] = await Promise.all([
        supabase.from('inquiry_contracts').select('*').order('created_at', { ascending: false }),
        supabase.from('contract_tc_templates').select('*').order('label'),
      ]);
      if (cErr) throw cErr;
      if (tErr) throw tErr;
      setContracts((cData || []).map(mapContract));
      setTcTemplates((tData || []).map(mapTemplate));
    } catch (err) {
      console.error('[useSupabaseContracts] fetch error:', err);
      setError(formatSupabaseError(err, { action: 'fetch', entity: 'contracts' }));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const createContract = async (data) => {
    try {
      await requireSession();
      const { data: row, error: err } = await supabase
        .from('inquiry_contracts')
        .insert(toContractDb(data))
        .select('*')
        .single();
      if (err) throw err;
      const mapped = mapContract(row);
      setContracts(prev => [mapped, ...prev]);
      return { success: true, data: mapped };
    } catch (err) {
      return { success: false, error: formatSupabaseError(err, { action: 'create', entity: 'contract' }) };
    }
  };

  const updateContract = async (id, updates) => {
    try {
      await requireSession();
      const { data: row, error: err } = await supabase
        .from('inquiry_contracts')
        .update(toContractDb(updates))
        .eq('id', id)
        .select('*')
        .single();
      if (err) throw err;
      const mapped = mapContract(row);
      setContracts(prev => prev.map(c => (c.id === normalizeId(id) ? mapped : c)));
      return { success: true, data: mapped };
    } catch (err) {
      return { success: false, error: formatSupabaseError(err, { action: 'update', entity: 'contract' }) };
    }
  };

  const duplicateContract = async (id) => {
    try {
      await requireSession();
      const source = contracts.find(c => c.id === normalizeId(id));
      if (!source) throw new Error('Contract not found');
      const { id: _id, createdAt: _ca, updatedAt: _ua, ...rest } = source;
      const copy = {
        ...rest,
        contractName: source.contractName ? source.contractName + ' (copy)' : '',
        versionName: source.versionName || '',
        status: 'draft',
      };
      const { data: row, error: err } = await supabase
        .from('inquiry_contracts')
        .insert(toContractDb(copy))
        .select('*')
        .single();
      if (err) throw err;
      const mapped = mapContract(row);
      setContracts(prev => [mapped, ...prev]);
      return { success: true, data: mapped };
    } catch (err) {
      return { success: false, error: formatSupabaseError(err, { action: 'duplicate', entity: 'contract' }) };
    }
  };

  const deleteContract = async (id) => {
    try {
      await requireSession();
      const { error: err } = await supabase.from('inquiry_contracts').delete().eq('id', id);
      if (err) throw err;
      setContracts(prev => prev.filter(c => c.id !== normalizeId(id)));
      return { success: true };
    } catch (err) {
      return { success: false, error: formatSupabaseError(err, { action: 'delete', entity: 'contract' }) };
    }
  };

  const createTcTemplate = async (data) => {
    try {
      await requireSession();
      const { data: row, error: err } = await supabase
        .from('contract_tc_templates')
        .insert(toTemplateDb(data))
        .select('*')
        .single();
      if (err) throw err;
      const mapped = mapTemplate(row);
      setTcTemplates(prev => [...prev, mapped]);
      return { success: true, data: mapped };
    } catch (err) {
      return { success: false, error: formatSupabaseError(err, { action: 'create', entity: 'T&C template' }) };
    }
  };

  const updateTcTemplate = async (id, updates) => {
    try {
      await requireSession();
      const { data: row, error: err } = await supabase
        .from('contract_tc_templates')
        .update(toTemplateDb(updates))
        .eq('id', id)
        .select('*')
        .single();
      if (err) throw err;
      const mapped = mapTemplate(row);
      setTcTemplates(prev => prev.map(t => (t.id === normalizeId(id) ? mapped : t)));
      return { success: true, data: mapped };
    } catch (err) {
      return { success: false, error: formatSupabaseError(err, { action: 'update', entity: 'T&C template' }) };
    }
  };

  const deleteTcTemplate = async (id) => {
    try {
      await requireSession();
      const { error: err } = await supabase.from('contract_tc_templates').delete().eq('id', id);
      if (err) throw err;
      setTcTemplates(prev => prev.filter(t => t.id !== normalizeId(id)));
      return { success: true };
    } catch (err) {
      return { success: false, error: formatSupabaseError(err, { action: 'delete', entity: 'T&C template' }) };
    }
  };

  return {
    contracts,
    tcTemplates,
    loading,
    error,
    createContract,
    updateContract,
    duplicateContract,
    deleteContract,
    createTcTemplate,
    updateTcTemplate,
    deleteTcTemplate,
    refresh,
  };
};

export default useSupabaseContracts;
